'use client';

import React, { useEffect, useRef } from 'react';

const services = [
  {
    number: '01',
    title: 'Car Event Coverage',
    description:
      'Full-day or half-day coverage of car meets, shows, rallies, and track days. Candid crowd energy, detail shots, and hero frames of the builds that deserve to be remembered.',
    tags: ['Car Meets', 'Shows', 'Rallies', 'Track Days'],
  },
  {
    number: '02',
    title: 'Social & Lifestyle Photography',
    description:
      'Beyond the cars — the culture, the people, the moments between moments. Content built for Instagram, editorial use, or personal archives. Shot with the same editorial eye.',
    tags: ['Instagram Content', 'Editorial', 'Portraits', 'Culture'],
  },
  {
    number: '03',
    title: 'Videography & Reels',
    description:
      'Short-form video content for social media, event recaps, and brand storytelling. Cinematic edits that capture the sound, motion, and atmosphere of the scene.',
    tags: ['Reels', 'Event Recaps', 'Brand Video', 'Short-Form'],
  },
  {
    number: '04',
    title: 'Creative Direction',
    description:
      'For brands, builders, and event organizers who need more than a shooter — a visual identity. Concept development, shot lists, and post-production direction.',
    tags: ['Brand Identity', 'Concept', 'Post-Production', 'Consulting'],
  },
];

export default function TrackRecordSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal-hidden');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.remove('reveal-hidden');
                el.classList.add('reveal-visible');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-20 sm:py-28 md:py-36 bg-charcoal/20">
      <div className="max-w-8xl mx-auto px-4 sm:px-8 md:px-14">

        {/* Section label */}
        <div className="flex items-center gap-4 sm:gap-6 mb-12 sm:mb-16 md:mb-20 reveal-hidden">
          <span className="text-gold text-[0.62rem] tracking-[0.28em] uppercase font-medium whitespace-nowrap">
            02 — Services
          </span>
          <div className="flex-1 h-px bg-parchment/8" />
        </div>

        {/* Asymmetric layout: declaration left, cards right */}
        <div className="grid grid-cols-1 md:grid-cols-asymmetric-rev gap-0">

          {/* Narrow — declaration */}
          <div className="md:pr-16 mb-10 md:mb-0 flex flex-col justify-between py-4">
            <div>
              <blockquote className="pull-quote mb-8 sm:mb-12 reveal-hidden">
                <p className="font-serif text-parchment text-display-md italic font-light leading-[1.15]">
                  The shot exists for a second. I make it last.
                </p>
              </blockquote>
              <p className="text-parchment/45 text-sm font-light leading-relaxed tracking-wide reveal-hidden reveal-delay-1">
                Every service is built around one principle: the image should feel
                like you were there. Whether it&apos;s a 200-car meet or a single
                build shoot, the approach doesn&apos;t change.
              </p>
            </div>

            <div className="mt-10 sm:mt-16 reveal-hidden reveal-delay-2">
              <a href="https://ig.me/m/knxckout.media" target="_blank" rel="noopener noreferrer" className="gold-btn inline-flex w-full sm:w-auto justify-center sm:justify-start">
                <span>Book a Shoot</span>
              </a>
            </div>
          </div>

          {/* Wide — service cards */}
          <div className="md:pl-16 border-t md:border-t-0 md:border-l border-parchment/8 pt-2 md:pt-0">
            <div className="space-y-0">
              {services?.map((service, i) => (
                <div
                  key={service?.number}
                  className={`group py-7 sm:py-8 border-b border-parchment/8 reveal-hidden reveal-delay-${Math.min(i + 1, 4)}`}
                >
                  <div className="flex items-start gap-4 sm:gap-6">
                    <span className="text-gold/40 text-[0.65rem] tracking-[0.16em] font-medium mt-1 shrink-0">
                      {service?.number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-parchment text-lg sm:text-xl md:text-2xl font-light mb-2 sm:mb-3 group-hover:text-gold transition-colors duration-300">
                        {service?.title}
                      </h3>
                      <p className="text-parchment/45 text-sm font-light leading-relaxed tracking-wide mb-4 sm:mb-5">
                        {service?.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {service?.tags?.map((tag) => (
                          <span
                            key={tag}
                            className="text-[0.6rem] tracking-[0.12em] uppercase text-parchment/30 border border-parchment/10 px-2.5 py-1"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}